import React, { useRef, useState } from 'react';
import { Box, Button, Skeleton, useMediaQuery } from '@mui/material';
import Carousel from 'react-multi-carousel';

import BlockHeader from '../BlockHeader';
import BlockSubHeader from '../BlockSubHeader';
import ContentContainer from '../ContentContainer';
import { CathleteCard } from './CathleteCard';
import { CathleteResponse } from '../../../providers/DataProvider.types';

import { ReactComponent as Arrow } from '../../../assets/common/arrow_left.svg';

export const TopCathletes = ({
    top10Cathletes,
    lastModified,
    headerText,
    subHeaderText,
    mode,
}: {
    top10Cathletes: Array<{ clanPicture?: string } & CathleteResponse> | undefined;
    lastModified: Date | undefined;
    headerText: string;
    subHeaderText: string;
    mode: 'topClan' | 'topServer';
}) => {
    const carouselRef = useRef<Carousel>(null);
    const [nextSlide, setNextSlide] = useState(0);
    const isMobile = useMediaQuery('(max-width:1200px)');

    const carouselResponsive = {
        superLargeDesktop: {
            // the naming can be any, depends on you.
            breakpoint: { max: 4000, min: 3000 },
            items: isMobile ? 2 : 3,
        },
        desktop: {
            breakpoint: { max: 3000, min: 1024 },
            items: isMobile ? 2 : 3,
        },
        tablet: {
            breakpoint: { max: 1024, min: 464 },
            items: isMobile ? 2 : 3,
        },
        mobile: {
            breakpoint: { max: 464, min: 0 },
            items: isMobile ? 2 : 3,
        },
    };

    return (
        <ContentContainer withoutMargin colorBg>
            <Box
                display={'flex'}
                justifyContent={'space-between'}
                sx={{
                    padding: '0 20px',
                }}
            >
                <Box>
                    <BlockHeader text={headerText}></BlockHeader>
                    <BlockSubHeader text={subHeaderText} time={lastModified}></BlockSubHeader>
                </Box>
                <Box>
                    <Button
                        disabled={nextSlide === 0}
                        sx={{
                            border: '1px solid #D8DFE5',
                            borderRadius: '50%',
                            width: '64px',
                            height: '64px',
                            color: '#0F0E0E',
                            '&:hover': {
                                border: '1px solid #C6CFD8',
                                background: 'transparent',
                            },
                            '&:disabled': {
                                '& path': {
                                    stroke: '#D8DFE5',
                                },
                            },
                        }}
                        onClick={() => {
                            if (carouselRef && carouselRef.current) {
                                carouselRef.current.goToSlide(nextSlide - 1);
                            }
                        }}
                    >
                        <Arrow></Arrow>
                    </Button>
                    <Button
                        disabled={nextSlide === (isMobile ? 8 : 7)}
                        sx={{
                            border: '1px solid #D8DFE5',
                            borderRadius: '50%',
                            width: '64px',
                            height: '64px',
                            color: '#0F0E0E',
                            transform: 'rotate(180deg)',
                            marginLeft: '10px',
                            '&:hover': {
                                border: '1px solid #C6CFD8',
                                background: 'transparent',
                            },
                            '&:disabled': {
                                '& path': {
                                    stroke: '#D8DFE5',
                                },
                            },
                        }}
                        onClick={() => {
                            if (carouselRef && carouselRef.current) {
                                carouselRef.current.goToSlide(nextSlide + 1);
                            }
                        }}
                    >
                        <Arrow></Arrow>
                    </Button>
                </Box>
            </Box>

            <Box
                sx={{
                    position: 'relative',
                    maxWidth: '100%',
                    marginBottom: '80px',
                    padding: '0 20px 0 0',
                    cursor: 'grab',

                    '& .carousel': {
                        overflow: 'visible',
                        '& li': {
                            transition: 'all ease 0.5s',
                        },
                        '& li[aria-hidden="true"]': {
                            opacity: isMobile ? 1 : 0.2,
                        },
                    },
                }}
            >
                {top10Cathletes ? (
                    <Carousel
                        className="carousel"
                        ref={carouselRef}
                        arrows={false}
                        beforeChange={(nextSlide) => {
                            setNextSlide(nextSlide);
                        }}
                        responsive={carouselResponsive}
                    >
                        {top10Cathletes?.map((cathlete, index) => {
                            return (
                                <CathleteCard
                                    mode={mode}
                                    key={cathlete.username}
                                    index={index}
                                    cathlete={cathlete}
                                ></CathleteCard>
                            );
                        })}
                    </Carousel>
                ) : (
                    <Skeleton
                        sx={{ borderRadius: '20px', margin: '0 20px' }}
                        variant="rounded"
                        width={'100%'}
                        height={'217px'}
                    />
                )}
            </Box>
        </ContentContainer>
    );
};
